import { useNavigate } from 'react-router-dom';
import { useTheme } from './useTheme';
import { ThemeSwitch } from './ThemeSwitch';
import s from './WhatIsThis.module.css';

// The standalone "What is TPIAP?" splash — a proper explainer that sells the game.
// Reachable pre-login (linked from the Landing) and signed-in (from the leagues hub).
// Rendered outside the app shell, so it wraps its own themed root.

const STEPS = [
  { icon: '🎵', h: 'Drop a track', p: 'Every round has a theme — “one-hit wonders”, “songs about rain”, whatever. Everyone submits one entry: a song, a video, or an image. Anonymously.' },
  { icon: '🗳️', h: 'Rank & guess', p: 'Spend a points budget to rank everyone else’s picks. Then play detective: guess who submitted what. Hate one? Veto it.' },
  { icon: '🏆', h: 'Climb the boards', p: 'Score for great picks, for guessing right, and for keeping people guessing about you. Three leaderboards, one running argument in the chat.' },
];

const MODES = [
  { h: 'DJ mode', p: 'One person runs the show — you set every round’s theme and pace. Great for a curated season.' },
  { h: 'Jukebox mode', p: 'Everyone proposes rounds; they’re drawn into a random running order. The whole crew builds the season together.' },
];

export function WhatIsThis({ signedIn }: { signedIn: boolean }) {
  const { mode } = useTheme();
  const navigate = useNavigate();
  return (
    <div className="tp-root" data-mode={mode}>
      <div className={s.page}>
        <header className={s.top}>
          <button className={s.brand} onClick={() => navigate(signedIn ? '/leagues' : '/')}>
            <span className={s.mk}><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M9 18V6l10-2v12" /><circle cx="6" cy="18" r="3" /><circle cx="16" cy="16" r="3" /></svg></span>TPIAP
          </button>
          <div className={s.topr}>
            <ThemeSwitch />
            <button className={s.back} onClick={() => navigate(signedIn ? '/leagues' : '/')}>{signedIn ? '← Your leagues' : '← Sign in'}</button>
          </div>
        </header>

        <section className={s.hero}>
          <span className={s.eyebrow}>This Place Is A Playlist</span>
          <h1>A music-and-media guessing league for you and your mates.</h1>
          <p className={s.lede}>Your crew, one playlist a round. Submit a track to the theme, score everyone else’s, and try to guess who picked what — all while staying hidden yourself. It’s part mixtape, part detective game, part group chat.</p>
          <div className={s.cta}>
            <button className={s.primary} onClick={() => navigate(signedIn ? '/leagues' : '/')}>{signedIn ? 'Go to your leagues →' : 'Get started — it’s free →'}</button>
          </div>
        </section>

        <section className={s.section}>
          <h2>How a round works</h2>
          <div className={s.steps}>
            {STEPS.map((st) => (
              <div key={st.h} className={s.step}>
                <span className={s.stepicon}>{st.icon}</span>
                <b>{st.h}</b>
                <p>{st.p}</p>
              </div>
            ))}
          </div>
        </section>

        <section className={s.section}>
          <h2>Two ways to play</h2>
          <div className={s.modes}>
            {MODES.map((m) => (
              <div key={m.h} className={s.modecard}>
                <b>{m.h}</b>
                <p>{m.p}</p>
              </div>
            ))}
          </div>
        </section>

        <section className={s.section}>
          <h2>The good bits</h2>
          <ul className={s.bullets}>
            <li><b>Anonymous submissions</b> — no one knows a pick is yours until the reveal.</li>
            <li><b>Three leaderboards</b> — best taste, best detective, best at hiding.</li>
            <li><b>Your pace</b> — rapid (a day a phase), steady (a few days), or slow (weekly).</li>
            <li><b>Just email to join</b> — a sign-in link, no password, no app to install.</li>
          </ul>
        </section>

        <section className={s.footcta}>
          <h2>Ready to start an argument about music?</h2>
          <button className={s.primary} onClick={() => navigate(signedIn ? '/leagues' : '/')}>{signedIn ? 'Back to your leagues →' : 'Email me a sign-in link →'}</button>
          <p className={s.fine}>Free · runs in your browser · invite-only leagues.</p>
        </section>
      </div>
    </div>
  );
}
